import { Box, Button, Divider, Grid2, Link, Typography, Tooltip } from "@mui/material";
import React, { useState } from "react";
import PrimaryIcon from "Components/Common/PrimaryIcon";
import { ReactComponent as PlayIcon } from "Assets/Icons/Home/PlayIcon.svg";
import { ReactComponent as CalendarIcon } from "Assets/Icons/Home/CalendarIcon.svg";
import { ReactComponent as ClockIcon } from "Assets/Icons/Home/ClockIcon.svg";
import { ReactComponent as CoinIcon } from "Assets/Icons/Home/CoinIcon.svg";
import { ReactComponent as DegreeIcon } from "Assets/Icons/Classroom/DegreeIcon.svg";
import { ReactComponent as DownloadIcon } from "Assets/Icons/Classroom/DownloadIcon.svg";
import { ReactComponent as DocumentIcon } from "Assets/Icons/Classroom/DocumentIcon.svg";
import Warning from "Components/Common/Warning";
import PrimarySlider from "Components/Common/PrimarySlider";
import { SwiperSlide } from "swiper/react";
import { useDispatch, useSelector } from "react-redux";
import { joinSession } from "Slices/thunks";
import LectureDialog from "../ClassDialogs/LectureDialog";

const sessionStatusObj = {
  1: "Pending",
  2: "OnGoing",
  3: "Completed",
  4: "Cancelled"
}


const styles = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  "& .degreewarn": {
    display: "flex",
    gap: "8px",
  },
  "& .iconBack": {
    background: theme.palette.background.default,
    padding: "6px 3px",
    borderRadius: "8px",
  },
  "& .lectureBox": {
    border: `1px solid ${theme.palette.border.blue}`,
    borderRadius: "8px",
    padding: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "& .start": {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    "& .end": {
      display: "flex",
      alignItems: "center",
      gap: "16px",
    },
  },
});

const colors = [
  "#418DF0",
  "#26B673",
  "#EF8DEC",
  "#58CEF9",
  "#F04178",
  "#418DF0",
  "#26B673",
  "#EF8DEC",
  "#58CEF9",
  "#F04178",
];

const SessionTab = ({ session }) => {
  const dispatch = useDispatch();
  const [viewRecording, setViewRecording] = useState(false);

  const { Profile, Translation } = useSelector((state) => ({
    Profile: state.Profile.profile,
    Translation: state.Translation.translation?.Course || {},
  }));

  function formatDateToDayMonth(dateString) {
    const date = new Date(dateString);
    
    const weekday = date.toLocaleString('en-US', { weekday: 'short' });
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });

    return `${weekday}, ${day} ${month}`;
  }

  function formatTimeWithIntl(dateString) {
    const date = new Date(dateString);

    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date).toLowerCase(); // toLowerCase for 'pm'
  }

  const handleSession = (session) => {
    if (session.SessionStatusID != 2) {
      return
    }

    const url = `https://whiteboard.codedplus.com/join-session/${Profile.UID}/channellive_s${session.ID}_b${session.BatchID}/GC/3/${Profile?.FullName}`;
    window.open(url, '_blank')

    // window.location.href = `http://localhost:5173/join-session/${Profile.UID}/channel_s${189}_b${session.BatchID}/QC/3/${Profile?.FullName}`;
    // window.location.href = `http://localhost:5173/join-session/${Profile.UID}/channel_s${session.ID}_b${session.ID}/QC/3/${Profile?.FullName}`;
  }

  return (
    <Grid2 sx={styles}>
      <Box display={"flex"} gap="16px">
        <Box
          onClick={() => setViewRecording(true)}
          sx={(theme) => ({
            background: theme.palette.primary.main,
            borderRadius: "8px",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
            cursor: "pointer",
          })}
        >
          <Typography color="white">{Translation?.ProbabilityBasicDefinitions}</Typography>
          { session.RecordingURL && <>
            <PrimaryIcon icon={PlayIcon} width="30px" height="30px" cursor="pointer" />
            <Divider
              flexItem
              sx={{ background: "#fff", height: "2px", marginTop: "auto" }}
            />
          </>
          }
        </Box>
        <Box display={"flex"} flexDirection={"column"} gap={"16px"}>
          <Typography color="primary" fontWeight={500} variant="title">
            {Translation?.SessionRecording}
          </Typography>
          <Box>
            <Typography variant="subtitle" fontWeight={500}>
              {/* Day 123 - Probability - Basic Definitions */}
              {`${Translation?.Day} ${session?.Day}`}
            </Typography>
            <Box
              mt="10px"
              sx={{
                "&>p": {
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                },
              }}
              display={"flex"}
              alignItems={"center"}
              gap="10px"
            >
              <Typography color="secondary.light">
                <PrimaryIcon height="18px" width="18px" icon={CalendarIcon} />
                {/* Sat, 26 Oct */}
                {formatDateToDayMonth(session?.StartDate)}
              </Typography>
              <Typography color="secondary.light">
                <PrimaryIcon height="18px" width="18px" icon={ClockIcon} />
                {/* 09:00 pm */}
                {formatTimeWithIntl(session?.StartDate)}
              </Typography>
            </Box>
          </Box>
          <Box display={"flex"} flexDirection={"row"} gap={"16px"}>
            { session.RecordingURL &&
              <Button
                sx={{ width: "fit-content" }}
                variant="outlined"
                color="error"
                onClick={() => setViewRecording(true)}
              >
                {Translation?.WatchRecording}
              </Button> 
            }
            { session.SessionStatusID <= 2 &&
              <Tooltip title={
                session.SessionStatusID === 1 ? Translation?.SessionNotStartedYet :
                session.SessionStatusID === 2 ? Translation?.SessionStarted :
                session.SessionStatusID === 3 ? Translation?.SessionCompleted :
                session.SessionStatusID === 4 ? Translation?.SessionCancelled : ""
              }>
                <Button
                  sx={{ width: "fit-content" }}
                  variant="outlined"
                  color="primary"
                  onClick={() => handleSession(session)}
                  disabled={session.SessionStatusID != 2}
                >
                  {Translation?.JoinSession}
                </Button>
              </Tooltip>
            }
          </Box>
        </Box>
        <Box sx={{ marginTop: "auto", marginLeft: "auto" }}>
          <Typography
            display={"flex"}
            alignItems={"center"}
            gap="5px"
            fontWeight={500}
          >
            <PrimaryIcon icon={CoinIcon} fill={false} />{`3 ${Translation?.Coins}`}
          </Typography>
          <Typography color="secondary.light">
            {Translation?.WatchArchivedSession}
          </Typography>
        </Box>
      </Box>
      <Box className="degreewarn">
        <PrimaryIcon
          className="iconBack"
          icon={DegreeIcon}
          width="18px"
          height="12px"
        />
        <Typography color="secondary.light">
          {Translation?.ReadDegreeContent}
        </Typography>
      </Box>
      <Box display={"flex"} flexDirection={"column"} gap={"10px"}>
        <Box className="lectureBox">
          <Box className="start">
            <PrimaryIcon
              className="iconBack"
              icon={DocumentIcon}
              color="primary"
              width="18px"
              height="12px"
            />
            <Typography
              variant="subtitle"
              fontWeight={500}
              color="secondary.light"
            >
              {Translation?.PostLecture}
            </Typography>
          </Box>
          <Box className="end">
            <Link color="primary" href="#" fontWeight={500} underline="none">
              {" "}
              {`${Translation?.Lecture} 1`}
            </Link>
            <Link color="primary" href="#" fontWeight={500} underline="none">
              {" "}
              {Translation?.RevisionNotes}
            </Link>
          </Box>
        </Box>
        <Box className="lectureBox">
          <Box className="start">
            <PrimaryIcon
              className="iconBack"
              icon={DocumentIcon}
              color="primary"
              width="18px"
              height="12px"
            />
            <Typography
              variant="subtitle"
              fontWeight={500}
              color="secondary.light"
            >
              {Translation?.LectureNotes}
            </Typography>
          </Box>
          <Box className="end">
            <Typography color="secondary.light">
              {Translation?.DownloadInstructorNotes}
            </Typography>
            <Button
              endIcon={
                <PrimaryIcon icon={DownloadIcon} width="16px" height="16px" />
              }
              color="secondary"
              variant="outlined"
            >
              {Translation?.Download}
            </Button>
          </Box>
        </Box>
      </Box>
      {/* <Box display={"flex"} flexDirection={"column"} gap={"10px"}>
        <Box className="lectureBox">
          <Box className="start">
            <PrimaryIcon
              className="iconBack"
              icon={DocumentIcon}
              color="primary"
              width="18px"
              height="12px"
            />
            <Typography
              variant="subtitle"
              fontWeight={500}
              color="secondary.light"
            >
              Post-lecture Content
            </Typography>
          </Box>
          <Box className="end">
            <Link color="primary" href="#" fontWeight={500} underline="none">
              {" "}
              Lecture 1
            </Link>
            <Link color="primary" href="#" fontWeight={500} underline="none">
              {" "}
              Revision Notes
            </Link>
          </Box>
        </Box>
        <Box className="lectureBox">
          <Box className="start">
            <PrimaryIcon
              className="iconBack"
              icon={DocumentIcon}
              color="primary"
              width="18px"
              height="12px"
            />
            <Typography
              variant="subtitle"
              fontWeight={500}
              color="secondary.light"
            >
              Lecture Notes
            </Typography>
          </Box>
          <Box className="end">
            <Typography color="secondary.light">
              Download instructor notes from here
            </Typography>
            <Button
              endIcon={
                <PrimaryIcon icon={DownloadIcon} width="16px" height="16px" />
              }
              color="secondary"
              variant="outlined"
            >
              Download
            </Button>
          </Box>
        </Box>
      </Box> */}
      <Warning showAlert={false}>
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          width={"100%"}
        >
          <Box>
            <Typography fontWeight={500} variant="subtitle">
              {Translation?.GetHelpToUnderstandTopic}
            </Typography>
            <Typography color="secondary.light">
              {Translation?.RaiseVideoCallHelpRequest}
            </Typography>
          </Box>
          <Button variant="outlined" color="secondary">
            {Translation?.VideoCallHelpRequest}
          </Button>
        </Box>
      </Warning>
      <Typography variant="title" fontWeight={500}>
        {Translation?.SimilarSessions}
      </Typography>
      <Box width={"100%"}>
        <PrimarySlider spacing={"15px"}>
          {colors.map((item, i) => {
            return (
              <SwiperSlide>
                <Box
                  sx={(theme) => ({
                    background: item,
                    borderRadius: "8px",
                    padding: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                    alignItems: "center",
                    cursor: "pointer",
                  })}
                >
                  <Typography color="white">
                    {Translation?.ProbabilityBasicDefinitions}
                  </Typography>
                  <PrimaryIcon icon={PlayIcon} width="30px" height="30px" />
                  <Divider
                    flexItem
                    sx={{
                      background: "#fff",
                      height: "2px",
                      marginTop: "auto",
                    }}
                  />
                </Box>
              </SwiperSlide>
            );
          })}
        </PrimarySlider>
      </Box>
      <LectureDialog open={viewRecording} onClose={setViewRecording} session={session}/>
    </Grid2>
  );
};

export default SessionTab;
